/**
 * RC 1.45.PRELAUNCH — Consent-safe analytics wrapper.
 *
 * All product-event telemetry in the app MUST go through this module.
 * It guarantees:
 *
 *   1. Events only fire when `window.__ndorAnalyticsConsent === true`
 *      (the gate set by `public/index.html` from the
 *      `ndorflow_consent` record written by the marketing-site
 *      CookieConsent banner). No consent → no-op.
 *
 *   2. Events only fire when PostHog actually loaded on the page — the
 *      loader is skipped entirely when consent is false, so
 *      `window.posthog` will be `undefined` on no-consent sessions.
 *      We never lazy-load PostHog client-side after the fact; consent
 *      is collected and the page reloads to pick up the stub.
 *
 *   3. No PII in event properties — emit keys like `plan_code`,
 *      `action_type`, durations and booleans. Never email, name,
 *      raw CV text, JD text, etc. Call sites are expected to honour
 *      this; the utility rejects objects containing obviously-PII
 *      keys as a defensive layer.
 *
 *   4. Identify is optional and only ever uses a stable backend user
 *      id (never email). When consent flips, we reset the session so
 *      we never merge a pre-consent anonymous id into an identified
 *      post-consent profile.
 *
 * Event-name contract (frozen by RC 1.45.PRELAUNCH):
 *
 *   signup_started                 — user reached the signup form
 *   signup_completed               — user created an account
 *   onboarding_completed           — single-step onboarding save OK
 *   plan_selected                  — user clicked Choose on a paid plan
 *   plan_activated                 — entitlement.status === 'active' first observed
 *   plan_handoff_seen              — /plan/activated handoff rendered
 *   plan_handoff_cta_clicked       — user clicked the handoff CTA
 *   dashboard_first_action         — user's first paid action in session
 *   preferences_updated            — user saved candidate preferences
 *   analyze_fit_run                — Analyse Fit action completed
 *   tailored_cv_generated          — Tailored CV action completed
 *   cover_letter_generated         — Cover letter action completed
 *   application_pack_generated     — Application pack assembled
 *
 * Invariant: adding new event names requires updating this header and
 * `/app/docs/system_state/rc_1_45_prelaunch_pricing_billing_website_parity_audit.md`
 * in the same PR.
 */

// Very small defensive PII allow-list (reject obviously-PII keys in props).
const FORBIDDEN_PROP_KEYS = new Set([
  'email',
  'user_email',
  'name',
  'full_name',
  'first_name',
  'last_name',
  'password',
  'cv_text',
  'jd_text',
  'job_description',
  'resume_text',
]);

function _consentGranted() {
  return typeof window !== 'undefined' && window.__ndorAnalyticsConsent === true;
}

function _posthogReady() {
  return (
    typeof window !== 'undefined' &&
    window.posthog &&
    typeof window.posthog.capture === 'function'
  );
}

function _stripPii(props) {
  if (!props || typeof props !== 'object') return props;
  const out = {};
  for (const k of Object.keys(props)) {
    if (FORBIDDEN_PROP_KEYS.has(k)) continue;
    out[k] = props[k];
  }
  return out;
}

/**
 * Capture a product event. No-op when consent is absent or PostHog
 * didn't load. Never throws.
 */
export function track(eventName, props = undefined) {
  try {
    if (!eventName || typeof eventName !== 'string') return;
    if (!_consentGranted()) return;
    if (!_posthogReady()) return;
    const clean = _stripPii(props);
    window.posthog.capture(eventName, clean);
  } catch {
    // Telemetry must never break the app.
  }
}

/**
 * Associate subsequent events with a backend user id. Call this once
 * per session after login. `userId` must be the stable DB id, never
 * email. No-op when consent is absent.
 */
export function identify(userId, personProps = undefined) {
  try {
    if (!userId) return;
    if (!_consentGranted()) return;
    if (!_posthogReady()) return;
    const clean = _stripPii(personProps);
    window.posthog.identify(String(userId), clean);
  } catch {
    /* never throw */
  }
}

/**
 * Reset the PostHog session (call on logout, or when consent is
 * revoked, so we don't merge anonymous→identified across users).
 */
export function resetAnalytics() {
  try {
    if (!_posthogReady()) return;
    window.posthog.reset();
  } catch {
    /* never throw */
  }
}

/**
 * Debug helper — returns whether analytics will actually fire.
 * Useful in dev, harmless in prod.
 */
export function analyticsReady() {
  return _consentGranted() && _posthogReady();
}

export default { track, identify, resetAnalytics, analyticsReady };
